import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TABS, TIPOS_ANALISE } from "../config";
import {
  api,
  ApiError,
  type AmostraProtocolo,
  type DetalheOsProtocolo,
  type EquipamentoBusca,
  type EtapaProtocolo,
  type OsProtocolo,
} from "../api";
import { formatDate } from "../format";
import { CONDICOES_OLEO_NORMA } from "../normas-vocabulario";
import { usePermissoes } from "../permissoes";
import ErrorState from "./ErrorState";
import RecordForm from "./RecordForm";
import { ModalEditarOs, ModalExcluirOs, ModalExcluirAmostra, type AmostraParaExcluir } from "./OsGerenciar";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconClose,
  IconCheck,
  IconAlert,
  IconInbox,
  IconDownload,
  IconRefresh,
  IconClipboard,
} from "../icons";

interface Props {
  onDataChanged: () => void;
  /**
   * Pedido de outra tela para abrir a "Nova OS" — o botão "Nova amostra" das
   * Análises manda para cá (amostra nova entra SEMPRE pelo Protocolo). `os`
   * preenchida = acrescentar equipamento a uma OS que já existe. `n` muda a
   * cada pedido, para o mesmo pedido duas vezes seguidas também abrir.
   */
  pedidoNovaOs?: { os: string | null; cliente: string | null; n: number } | null;
  /** O pedido foi aberto — o App o apaga (voltar à aba não reabre o pop-up). */
  onPedidoAtendido?: () => void;
}

/**
 * O PROTOCOLO — a porta de entrada das amostras (ver server/protocolo.ts).
 *
 * Dois níveis, como as Análises:
 *   1) as OS, com a situação de cada uma (as sete etapas do fluxo) — quem
 *      espera ENTRADA aparece primeiro, porque é o que depende de quem está
 *      no balcão;
 *   2) a OS aberta: a conferência de chegada (quantas o campo disse, quantas
 *      vieram, a data de entrada) e cada equipamento com os ensaios dele.
 *
 * A data de entrada é o único gatilho: é ela que faz a amostra aparecer nos
 * módulos de análise. Nada nesta tela lança resultado — isso é da bancada.
 */

const ROTULO_ORIGEM: Record<string, string> = {
  campo: "Campo",
  planejamento: "Planejamento",
  protocolo: "Laboratório",
};

/** Hoje no relógio de quem está na tela (YYYY-MM-DD). */
const hoje = (): string => new Date().toLocaleDateString("sv-SE");

function somarDias(iso: string, dias: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

/** Tom da situação: só "pendente" pede ação de quem está no balcão. */
function tomDaEtapa(e: EtapaProtocolo): string {
  if (e === "pendente") return "warn";
  if (e === "relatorio_gerado" || e === "diagnostico_concluido") return "ok";
  return "neutral";
}

const nomeEnsaio = (k: string) => TIPOS_ANALISE.find((t) => t.key === k)?.titulo ?? k;

/** "Físico-Químico (2) · PCB (1)" — para onde a entrada mandou as amostras. */
function textoDistribuicao(d: Record<string, number>): string {
  return TIPOS_ANALISE.filter((t) => d[t.key])
    .map((t) => `${t.titulo} (${d[t.key]})`)
    .join(" · ");
}

/** O equipamento numa linha: TAG · tipo · série · fabricante. */
function descreverEquip(e: {
  tag?: string | null;
  tipo_equipamento?: string | null;
  numero_serie?: string | null;
  fabricante?: string | null;
}): string {
  return [e.tag, e.tipo_equipamento, e.numero_serie ? `série ${e.numero_serie}` : null, e.fabricante]
    .filter(Boolean)
    .join(" · ");
}

/**
 * A etiqueta do ensaio (FQ, CR, PCB, 2-FALL). Fora do componente de propósito
 * (declarada dentro, o React remontaria todas a cada render).
 */
function SeloEnsaio({ chave, feito }: { chave: string; feito?: boolean }) {
  const t = TIPOS_ANALISE.find((x) => x.key === chave);
  if (!t) return null;
  return (
    <span
      className={`an-selo tom-${t.tom}${feito ? " prot-selo-feito" : ""}`}
      title={feito ? `${t.titulo} — ensaio concluído` : t.titulo}
    >
      {t.sigla}
      {feito ? " ✓" : ""}
    </span>
  );
}

/** Os quatro ensaios como botões de liga/desliga (criação e edição da coleta). */
function EscolhaEnsaios({ valor, onChange }: { valor: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="prot-ensaios">
      {TIPOS_ANALISE.map((t) => {
        const ativo = valor.includes(t.key);
        return (
          <button
            key={t.key}
            type="button"
            className={`prot-ensaio ${ativo ? "ativo" : ""}`}
            aria-pressed={ativo}
            onClick={() => onChange(ativo ? valor.filter((k) => k !== t.key) : [...valor, t.key])}
          >
            <span className={`an-selo tom-${t.tom}`}>{t.sigla}</span> {t.titulo}
          </button>
        );
      })}
    </div>
  );
}

/* ======================================================================== */
/*  BUSCA DE EQUIPAMENTO PELO Nº DE SÉRIE (com "cadastrar primeiro")         */
/* ======================================================================== */

function BuscaEquipamento({
  cliente,
  inicial,
  onEscolher,
  onCadastrar,
  autoFoco,
}: {
  cliente?: string;
  inicial?: string;
  onEscolher: (e: EquipamentoBusca) => void;
  /** Não achou: abre o cadastro do equipamento (com a série já digitada). */
  onCadastrar: (termo: string) => void;
  autoFoco?: boolean;
}) {
  const [q, setQ] = useState(inicial ?? "");
  const [itens, setItens] = useState<EquipamentoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const termo = q.trim();
    if (termo.length < 2) {
      setItens([]);
      setBuscou(false);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      setBuscando(true);
      api.protocolo
        .buscarEquipamentos(termo, cliente, ctrl.signal)
        .then((r) => {
          setItens(r.itens);
          setBuscou(true);
          setErro(null);
        })
        .catch((e) => {
          if ((e as Error)?.name !== "AbortError") {
            setErro(e instanceof ApiError ? e.message : "Não foi possível buscar no cadastro.");
          }
        })
        .finally(() => setBuscando(false));
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q, cliente]);

  return (
    <div className="prot-busca">
      <div className="search-wrap prot-busca-campo">
        <IconSearch size={17} />
        <input
          className="search"
          placeholder="Nº de série, TAG ou proprietário do equipamento"
          value={q}
          autoFocus={autoFoco}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {erro && <div className="form-error">{erro}</div>}
      {buscando && itens.length === 0 && <p className="prot-busca-nota">Procurando no cadastro...</p>}
      {itens.length > 0 && (
        <ul className="prot-busca-lista">
          {itens.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => onEscolher(e)}>
                <strong>{descreverEquip(e) || `Equipamento #${e.id}`}</strong>
                <span>
                  {e.cliente || "proprietário não informado"}
                  {e.local_instalacao ? ` · ${e.local_instalacao}` : ""}
                  {e.analises > 0 ? ` · ${e.analises} análise(s) no histórico` : " · sem análise anterior"}
                  {e.ativo === false ? " · inativo" : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {buscou && !buscando && itens.length === 0 && (
        <div className="prot-busca-vazia">
          <IconAlert size={15} />
          <span>
            Nenhum equipamento com “{q.trim()}” no cadastro. O equipamento precisa ser cadastrado antes de
            entrar na OS.
          </span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => onCadastrar(q.trim())}>
            <IconPlus size={15} /> Cadastrar equipamento
          </button>
        </div>
      )}
      {itens.length > 0 && (
        <button type="button" className="prot-link" onClick={() => onCadastrar(q.trim())}>
          Não está na lista? Cadastrar equipamento
        </button>
      )}
    </div>
  );
}

/** O formulário da aba Equipamentos, aberto por cima (cadastrar antes de vincular). */
const TAB_EQUIP = TABS.find((t) => t.key === "equipamentos");

function CadastroEquipamento({
  serie,
  cliente,
  onCancel,
  onCriado,
}: {
  serie: string;
  cliente: string;
  onCancel: () => void;
  onCriado: (e: EquipamentoBusca) => void;
}) {
  if (!TAB_EQUIP) return null;
  return (
    <RecordForm
      title="Novo equipamento"
      subtitle="A OS só aceita equipamento do cadastro. Cadastre aqui e ele já volta escolhido para a amostra."
      icon={TAB_EQUIP.icon}
      grupos={TAB_EQUIP.grupos}
      fields={TAB_EQUIP.fields}
      initial={{ numero_serie: serie, cliente }}
      onCancel={onCancel}
      onSubmit={async (data) => {
        const row = await api.create("equipamentos", data);
        onCriado({ ...(row as unknown as EquipamentoBusca), analises: 0 });
      }}
    />
  );
}

/* ======================================================================== */
/*  A CONFERÊNCIA DE ENTRADA                                                 */
/* ======================================================================== */

function PainelEntrada({
  detalhe,
  onRegistrado,
  onVincular,
}: {
  detalhe: DetalheOsProtocolo;
  onRegistrado: (mensagem: string) => void;
  onVincular: (a: AmostraProtocolo) => void;
}) {
  const pendentes = useMemo(() => detalhe.amostras.filter((a) => !a.entrada_lab_em), [detalhe]);
  /* Começa com as que PODEM entrar marcadas: a caixa normalmente chega inteira,
     e quem confere desmarca o que faltou. Sem equipamento não dá para marcar. */
  const [marcadas, setMarcadas] = useState<Set<number>>(
    () => new Set(pendentes.filter((a) => a.equipamento_id).map((a) => a.id)),
  );
  /* Amostra vinculada agora há pouco entra marcada (era a única coisa que faltava). */
  const vistas = useRef(new Set(pendentes.filter((a) => a.equipamento_id).map((a) => a.id)));
  const chaveVinculadas = pendentes.filter((a) => a.equipamento_id).map((a) => a.id).join(",");
  useEffect(() => {
    const novas = pendentes.filter((a) => a.equipamento_id && !vistas.current.has(a.id));
    if (novas.length === 0) return;
    for (const a of novas) vistas.current.add(a.id);
    setMarcadas((m) => new Set([...m, ...novas.map((a) => a.id)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveVinculadas]);

  const [data, setData] = useState(hoje());
  const [prevista, setPrevista] = useState(String(pendentes.length));
  /** null = acompanha o número de amostras marcadas (até a pessoa digitar). */
  const [recebida, setRecebida] = useState<string | null>(null);
  const [divergencia, setDivergencia] = useState("");
  const [observacao, setObservacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const idsMarcados = pendentes.filter((a) => marcadas.has(a.id)).map((a) => a.id);
  const recebidaEfetiva = recebida ?? String(idsMarcados.length);
  const diverge = prevista.trim() !== "" && Number(prevista) !== Number(recebidaEfetiva);
  const semEquip = pendentes.filter((a) => !a.equipamento_id);

  function alternar(id: number) {
    setMarcadas((m) => {
      const n = new Set(m);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function confirmar() {
    setErro(null);
    if (idsMarcados.length === 0) {
      setErro("Marque as amostras que chegaram.");
      return;
    }
    if (diverge && !divergencia.trim()) {
      setErro("Previsto e recebido não batem: descreva a divergência antes de confirmar.");
      return;
    }
    setSalvando(true);
    try {
      const r = await api.protocolo.receber(detalhe.os_numero, {
        data_entrada: data,
        quantidade_prevista: Number(prevista),
        quantidade_recebida: Number(recebidaEfetiva),
        divergencia: diverge ? divergencia.trim() : undefined,
        observacao: observacao.trim() || undefined,
        amostras: idsMarcados,
      });
      setRecebida(null);
      setDivergencia("");
      setObservacao("");
      onRegistrado(
        `Entrada registrada: ${r.liberadas} amostra(s) liberada(s) para ${textoDistribuicao(r.distribuicao)}.`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível registrar a entrada.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="prot-entrada">
      <header className="prot-entrada-topo">
        <span className="prot-entrada-ic">
          <IconInbox size={20} />
        </span>
        <div>
          <h4>Registrar a entrada no laboratório</h4>
          <p className="muted">
            Confira o que chegou, desmarque o que faltou e informe a data. A entrada libera cada amostra para os
            ensaios que ela pediu.
          </p>
        </div>
      </header>

      <ul className="prot-entrada-lista">
        {pendentes.map((a) => {
          const pode = !!a.equipamento_id;
          return (
            <li key={a.id} className={pode ? "" : "bloqueada"}>
              <label>
                <input
                  type="checkbox"
                  checked={marcadas.has(a.id) && pode}
                  disabled={!pode || salvando}
                  onChange={() => alternar(a.id)}
                />
                <span className="prot-entrada-nome">
                  <strong>{a.nome}</strong>
                  <em>{a.equip ? descreverEquip(a.equip) : "sem equipamento do cadastro"}</em>
                </span>
              </label>
              <span className="an-selos">
                {a.ensaios.map((k) => (
                  <SeloEnsaio key={k} chave={k} />
                ))}
              </span>
              {!pode && (
                <button type="button" className="btn-secondary btn-sm" onClick={() => onVincular(a)}>
                  Vincular equipamento
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {semEquip.length > 0 && (
        <p className="prot-aviso">
          <IconAlert size={14} /> {semEquip.length} amostra(s) sem equipamento do cadastro não podem entrar:
          vincule pelo nº de série (ou cadastre o equipamento primeiro).
        </p>
      )}

      <div className="prot-entrada-campos">
        <label className="field">
          <span>Data de entrada no laboratório</span>
          <input type="date" value={data} max={hoje()} onChange={(e) => setData(e.target.value)} />
        </label>
        <label className="field">
          <span>Quantidade prevista</span>
          <input
            type="number"
            min={0}
            value={prevista}
            onChange={(e) => setPrevista(e.target.value)}
            title="Quantas amostras a solicitação disse que viriam"
          />
        </label>
        <label className="field">
          <span>Quantidade recebida</span>
          <input
            type="number"
            min={0}
            value={recebidaEfetiva}
            onChange={(e) => setRecebida(e.target.value)}
            title="Quantas chegaram de fato (acompanha as marcadas até você digitar)"
          />
        </label>
      </div>
      {diverge && (
        <label className="field prot-divergencia">
          <span>
            Divergência — previstas {prevista || 0}, recebidas {recebidaEfetiva || 0}. O que aconteceu?
          </span>
          <textarea
            rows={2}
            value={divergencia}
            onChange={(e) => setDivergencia(e.target.value)}
            placeholder="ex.: 1 frasco quebrado no transporte; a amostra do TR-03 vem na próxima remessa"
          />
        </label>
      )}
      <label className="field">
        <span>Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="ex.: caixa chegou sem gelo"
        />
      </label>

      {erro && <div className="form-error">{erro}</div>}
      <div className="prot-entrada-acoes">
        <button className="btn-primary" onClick={() => void confirmar()} disabled={salvando || idsMarcados.length === 0}>
          <IconCheck size={17} /> {salvando ? "Registrando..." : `Confirmar recebimento (${idsMarcados.length})`}
        </button>
      </div>
    </section>
  );
}

/* ======================================================================== */
/*  POP-UPS: dados da coleta, vínculo do equipamento, nova OS                 */
/* ======================================================================== */

function ModalColeta({
  amostra,
  onCancel,
  onSalvo,
}: {
  amostra: AmostraProtocolo;
  onCancel: () => void;
  onSalvo: () => void;
}) {
  const pendente = !amostra.entrada_lab_em;
  const [numero, setNumero] = useState(amostra.numero_amostra ?? "");
  const [dataColeta, setDataColeta] = useState(amostra.data_coleta ?? "");
  const [temperatura, setTemperatura] = useState(amostra.temperatura_coleta ?? "");
  const [condicao, setCondicao] = useState(amostra.condicao_oleo ?? "");
  const [ensaios, setEnsaios] = useState<string[]>(amostra.ensaios_definidos ? amostra.ensaios : []);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setErro(null);
    if (pendente && ensaios.length === 0) {
      setErro("Escolha ao menos um ensaio.");
      return;
    }
    /*
     * SÓ O QUE MUDOU vai para o servidor. Mandar tudo apagava o que a tela não
     * soube mostrar: uma data da coleta escrita à mão como "07/10/26" abre o
     * campo em branco, e salvar só a temperatura gravaria a data vazia por cima.
     */
    const mudou: Parameters<typeof api.protocolo.atualizarColeta>[1] = {};
    if (numero !== (amostra.numero_amostra ?? "")) mudou.numero_amostra = numero;
    if (dataColeta !== (amostra.data_coleta ?? "")) mudou.data_coleta = dataColeta;
    if (temperatura !== (amostra.temperatura_coleta ?? "")) mudou.temperatura_coleta = temperatura;
    if (condicao !== (amostra.condicao_oleo ?? "")) mudou.condicao_oleo = condicao;
    const ensaiosAntes = amostra.ensaios_definidos ? amostra.ensaios : [];
    if (pendente && [...ensaios].sort().join() !== [...ensaiosAntes].sort().join()) mudou.analises = ensaios;
    if (Object.keys(mudou).length === 0) {
      onCancel();
      return;
    }
    setSalvando(true);
    try {
      await api.protocolo.atualizarColeta(amostra.id, mudou);
      onSalvo();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card prot-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Dados da coleta</h3>
            <p className="rf-head-sub">
              OS {amostra.os_numero} · {amostra.nome}
              {amostra.equip ? ` · ${descreverEquip(amostra.equip)}` : ""}
            </p>
          </div>
          <button className="icon-btn" onClick={onCancel} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <label className="field">
              <span>Nº da amostra / coleta</span>
              <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="ex.: 0427" />
            </label>
            <label className="field">
              <span>Data da coleta</span>
              <input type="date" value={dataColeta} max={hoje()} onChange={(e) => setDataColeta(e.target.value)} />
              {!amostra.data_coleta && amostra.data_coleta_texto && (
                <em className="rf-ajuda">Registrada como “{amostra.data_coleta_texto}” — fica assim se você não mudar.</em>
              )}
            </label>
            <label className="field">
              <span>Temperatura na coleta (°C)</span>
              <input
                value={temperatura}
                onChange={(e) => setTemperatura(e.target.value)}
                placeholder="quando aplicável"
              />
            </label>
            <label className="field">
              <span>Condição do óleo</span>
              <select value={condicao} onChange={(e) => setCondicao(e.target.value)}>
                <option value="">— não informada —</option>
                {CONDICOES_OLEO_NORMA.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {pendente ? (
              <div className="field field-wide">
                <span>Ensaios desta amostra</span>
                <EscolhaEnsaios valor={ensaios} onChange={setEnsaios} />
              </div>
            ) : (
              <p className="field-wide prot-nota">
                A amostra já está no laboratório: os ensaios dela mudam pelo cadastro da amostra, em Análises.
              </p>
            )}
          </div>
        </div>
        <div className="modal-foot">
          {erro && <div className="form-error">{erro}</div>}
          <div className="modal-foot-actions">
            <button className="btn-secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button className="btn-primary" onClick={() => void salvar()} disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalVincular({
  amostra,
  onCancel,
  onVinculado,
}: {
  amostra: AmostraProtocolo;
  onCancel: () => void;
  onVinculado: () => void;
}) {
  const [cadastrando, setCadastrando] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function vincular(e: EquipamentoBusca) {
    setErro(null);
    setSalvando(true);
    try {
      await api.protocolo.vincularEquipamento(amostra.id, e.id);
      onVinculado();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível vincular.");
      setSalvando(false);
    }
  }

  return (
    <>
      <div className="modal-overlay" onMouseDown={onCancel}>
        <div className="modal-card prot-modal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <div>
              <h3>Vincular equipamento</h3>
              <p className="rf-head-sub">
                OS {amostra.os_numero} · {amostra.nome}
                {amostra.serie_informada ? ` · série informada na coleta: ${amostra.serie_informada}` : ""}
              </p>
            </div>
            <button className="icon-btn" onClick={onCancel} title="Fechar">
              <IconClose size={18} />
            </button>
          </div>
          <div className="modal-body">
            <p className="modal-tab-hint">
              Escolha o equipamento do cadastro de onde saiu esta amostra. É o vínculo que traz o histórico dele
              para o diagnóstico — e a ficha dele completa a identificação do laudo.
            </p>
            <BuscaEquipamento
              autoFoco
              cliente={amostra.cliente ?? undefined}
              inicial={amostra.serie_informada ?? ""}
              onEscolher={(e) => void vincular(e)}
              onCadastrar={(termo) => setCadastrando(termo)}
            />
            {salvando && <p className="prot-busca-nota">Vinculando...</p>}
          </div>
          <div className="modal-foot">
            {erro && <div className="form-error">{erro}</div>}
            <div className="modal-foot-actions">
              <button className="btn-secondary" onClick={onCancel}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
      {cadastrando !== null && (
        <CadastroEquipamento
          serie={cadastrando}
          cliente={amostra.cliente ?? ""}
          onCancel={() => setCadastrando(null)}
          onCriado={(e) => {
            setCadastrando(null);
            void vincular(e);
          }}
        />
      )}
    </>
  );
}

/** Um equipamento na OS em criação. */
interface ItemNovo {
  chave: number;
  equip: EquipamentoBusca;
  ensaios: string[];
  numero: string;
  dataColeta: string;
  temperatura: string;
  condicao: string;
}

function ModalNovaOs({
  inicial,
  onCancel,
  onCriada,
}: {
  inicial: { os: string; cliente: string; existente: boolean };
  onCancel: () => void;
  onCriada: (os: string, mensagem: string) => void;
}) {
  const [os, setOs] = useState(inicial.os);
  const [cliente, setCliente] = useState(inicial.cliente);
  const [solicitante, setSolicitante] = useState("");
  // Prazo de retorno padrão: 15 dias, o mesmo que o Relatórios usa para as OS do campo.
  const [prazo, setPrazo] = useState(somarDias(hoje(), 15));
  const [itens, setItens] = useState<ItemNovo[]>([]);
  const [jaNoLab, setJaNoLab] = useState(true);
  const [dataEntrada, setDataEntrada] = useState(hoje());
  const [recebida, setRecebida] = useState<string | null>(null);
  const [divergencia, setDivergencia] = useState("");
  const [cadastrando, setCadastrando] = useState<string | null>(null);
  const [gerandoNumero, setGerandoNumero] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const chaveRef = useRef(1);
  /* Remonta a busca depois de cada escolha (zera o texto e a lista). */
  const [buscaKey, setBuscaKey] = useState(0);

  const recebidaEfetiva = recebida ?? String(itens.length);
  const diverge = jaNoLab && itens.length > 0 && Number(recebidaEfetiva) !== itens.length;

  function adicionar(e: EquipamentoBusca) {
    setItens((lista) => [
      ...lista,
      {
        chave: chaveRef.current++,
        equip: e,
        ensaios: [],
        numero: "",
        dataColeta: "",
        temperatura: "",
        condicao: "",
      },
    ]);
    if (!cliente.trim() && e.cliente) setCliente(e.cliente);
    setBuscaKey((k) => k + 1);
  }

  function mudar(chave: number, parcial: Partial<ItemNovo>) {
    setItens((lista) => lista.map((i) => (i.chave === chave ? { ...i, ...parcial } : i)));
  }

  async function gerarNumero() {
    setGerandoNumero(true);
    try {
      const r = await api.protocolo.proximoNumero();
      setOs(r.numero);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar o número.");
    } finally {
      setGerandoNumero(false);
    }
  }

  async function criar() {
    setErro(null);
    if (!os.trim()) return setErro("Informe o número da OS / protocolo.");
    if (!cliente.trim()) return setErro("Informe o cliente.");
    if (itens.length === 0) return setErro("Acrescente ao menos um equipamento (busque pelo nº de série).");
    const semEnsaio = itens.find((i) => i.ensaios.length === 0);
    if (semEnsaio) return setErro(`Escolha os ensaios de ${descreverEquip(semEnsaio.equip) || "um dos equipamentos"}.`);
    if (diverge && !divergencia.trim()) return setErro("Previsto e recebido não batem: descreva a divergência.");
    setSalvando(true);
    try {
      const r = await api.protocolo.criarOs({
        acrescentar: inicial.existente,
        os_numero: os.trim(),
        cliente: cliente.trim(),
        solicitante: solicitante.trim() || undefined,
        prazo_retorno: prazo || undefined,
        amostras: itens.map((i) => ({
          equipamento_id: i.equip.id,
          analises: i.ensaios,
          numero_amostra: i.numero.trim() || undefined,
          data_coleta: i.dataColeta || undefined,
          temperatura_coleta: i.temperatura.trim() || undefined,
          condicao_oleo: i.condicao || undefined,
        })),
        entrada: jaNoLab
          ? {
              data_entrada: dataEntrada,
              quantidade_prevista: itens.length,
              quantidade_recebida: Number(recebidaEfetiva),
              divergencia: diverge ? divergencia.trim() : undefined,
            }
          : null,
      });
      const ensaios = new Set(itens.flatMap((i) => i.ensaios));
      onCriada(
        r.os_numero,
        (r.os_existente
          ? `${r.ids.length} equipamento(s) acrescentado(s) à OS ${r.os_numero}`
          : `OS ${r.os_numero} criada com ${r.ids.length} equipamento(s)`) +
          (r.recebimento_id
            ? ` — entrada registrada; já está em ${[...ensaios].map(nomeEnsaio).join(", ")}.`
            : " — pendente de entrada no laboratório."),
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível criar a OS.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      {/* Sem fechar no clique fora: é um formulário longo, e um clique errado
          no fundo jogaria fora todos os equipamentos já escolhidos. */}
      <div className="modal-overlay">
        <div className="modal-card modal-wide prot-modal-os">
          <div className="modal-head rf-head">
            <div className="rf-head-txt">
              <span className="rf-head-icon">
                <IconInbox size={20} />
              </span>
              <div>
                <h3>{inicial.existente ? `Acrescentar equipamento à OS ${inicial.os}` : "Nova OS no laboratório"}</h3>
                <p className="rf-head-sub">
                  Para a amostra que chega direto ao laboratório. Depois de criada, a OS segue o mesmo caminho das
                  que vêm do campo.
                </p>
              </div>
            </div>
            <button className="icon-btn" onClick={onCancel} title="Fechar">
              <IconClose size={18} />
            </button>
          </div>

          <div className="modal-body rf-body">
            <section className="rf-secao">
              <div className="rf-secao-head">
                <span className="rf-secao-num">1</span>
                <h4>A OS</h4>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>Número da OS / protocolo</span>
                  <div className="rf-entrada">
                    <input
                      value={os}
                      onChange={(e) => setOs(e.target.value)}
                      disabled={inicial.existente}
                      placeholder="ex.: 2026-0458"
                    />
                    {!inicial.existente && (
                      <button
                        type="button"
                        className="rf-mini"
                        onClick={() => void gerarNumero()}
                        disabled={gerandoNumero}
                        title="Próximo número da sequência do laboratório (LAB-ano-nnnn)"
                      >
                        {gerandoNumero ? "..." : "Gerar nº"}
                      </button>
                    )}
                  </div>
                </label>
                <label className="field">
                  <span>Cliente</span>
                  <input value={cliente} onChange={(e) => setCliente(e.target.value)} />
                </label>
                <label className="field">
                  <span>Solicitante (opcional)</span>
                  <input
                    value={solicitante}
                    onChange={(e) => setSolicitante(e.target.value)}
                    placeholder="quem trouxe / pediu"
                  />
                </label>
                <label className="field">
                  <span>Prazo de retorno</span>
                  <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
                </label>
              </div>
            </section>

            <section className="rf-secao">
              <div className="rf-secao-head">
                <span className="rf-secao-num">2</span>
                <h4>Equipamentos e ensaios</h4>
                <span className="rf-secao-hint">pelo nº de série — só equipamento do cadastro</span>
              </div>
              <BuscaEquipamento
                key={buscaKey}
                cliente={cliente}
                onEscolher={adicionar}
                onCadastrar={(termo) => setCadastrando(termo)}
              />
              {itens.length === 0 ? (
                <p className="prot-nota">Nenhum equipamento ainda. Busque acima e clique para acrescentar.</p>
              ) : (
                <ol className="prot-itens">
                  {itens.map((i, n) => (
                    <li key={i.chave} className="prot-item">
                      <div className="prot-item-topo">
                        <span className="prot-item-num">{n + 1}</span>
                        <span className="prot-item-equip">
                          <strong>{descreverEquip(i.equip) || `Equipamento #${i.equip.id}`}</strong>
                          <em>{i.equip.cliente || "proprietário não informado"}</em>
                        </span>
                        <button
                          type="button"
                          className="icon-btn icon-btn-danger"
                          title="Tirar da OS"
                          onClick={() => setItens((l) => l.filter((x) => x.chave !== i.chave))}
                        >
                          <IconTrash size={16} />
                        </button>
                      </div>
                      <EscolhaEnsaios valor={i.ensaios} onChange={(v) => mudar(i.chave, { ensaios: v })} />
                      <div className="prot-item-campos">
                        <label className="field">
                          <span>Nº da amostra</span>
                          <input value={i.numero} onChange={(e) => mudar(i.chave, { numero: e.target.value })} />
                        </label>
                        <label className="field">
                          <span>Data da coleta</span>
                          <input
                            type="date"
                            value={i.dataColeta}
                            max={hoje()}
                            onChange={(e) => mudar(i.chave, { dataColeta: e.target.value })}
                          />
                        </label>
                        <label className="field">
                          <span>Temperatura (°C)</span>
                          <input
                            value={i.temperatura}
                            onChange={(e) => mudar(i.chave, { temperatura: e.target.value })}
                            placeholder="se aplicável"
                          />
                        </label>
                        <label className="field">
                          <span>Condição do óleo</span>
                          <select value={i.condicao} onChange={(e) => mudar(i.chave, { condicao: e.target.value })}>
                            <option value="">— não informada —</option>
                            {CONDICOES_OLEO_NORMA.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <section className="rf-secao">
              <div className="rf-secao-head">
                <span className="rf-secao-num">3</span>
                <h4>Entrada no laboratório</h4>
              </div>
              <label className="prot-check">
                <input type="checkbox" checked={jaNoLab} onChange={(e) => setJaNoLab(e.target.checked)} />
                <span>
                  As amostras já estão no laboratório — registrar a entrada agora (a OS vai direto para os ensaios)
                </span>
              </label>
              {jaNoLab && (
                <div className="form-grid">
                  <label className="field">
                    <span>Data de entrada</span>
                    <input
                      type="date"
                      value={dataEntrada}
                      max={hoje()}
                      onChange={(e) => setDataEntrada(e.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Quantidade recebida (previstas: {itens.length})</span>
                    <input
                      type="number"
                      min={0}
                      value={recebidaEfetiva}
                      onChange={(e) => setRecebida(e.target.value)}
                    />
                  </label>
                  {diverge && (
                    <label className="field field-wide">
                      <span>Divergência — o que aconteceu?</span>
                      <textarea rows={2} value={divergencia} onChange={(e) => setDivergencia(e.target.value)} />
                    </label>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="modal-foot">
            {erro && <div className="form-error">{erro}</div>}
            <div className="modal-foot-actions">
              <button className="btn-secondary" onClick={onCancel}>
                Cancelar
              </button>
              <button className="btn-primary" onClick={() => void criar()} disabled={salvando}>
                <IconCheck size={17} />{" "}
                {salvando ? "Salvando..." : inicial.existente ? "Acrescentar à OS" : "Criar OS"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {cadastrando !== null && (
        <CadastroEquipamento
          serie={cadastrando}
          cliente={cliente}
          onCancel={() => setCadastrando(null)}
          onCriado={(e) => {
            setCadastrando(null);
            adicionar(e);
          }}
        />
      )}
    </>
  );
}

/* ======================================================================== */
/*  A TELA                                                                   */
/* ======================================================================== */

type Filtro = "abertas" | "todas" | EtapaProtocolo;

export default function ProtocoloView({ onDataChanged, pedidoNovaOs, onPedidoAtendido }: Props) {
  const { podeExcluir } = usePermissoes();
  const [lista, setLista] = useState<OsProtocolo[]>([]);
  const [etapas, setEtapas] = useState<{ key: EtapaProtocolo; rotulo: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("abertas");
  const [busca, setBusca] = useState("");

  const [osAberta, setOsAberta] = useState<string | null>(null);
  const [detalhe, setDetalhe] = useState<DetalheOsProtocolo | null>(null);
  const [erroDetalhe, setErroDetalhe] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<number | null>(null);

  const [novaOs, setNovaOs] = useState<{ os: string; cliente: string; existente: boolean } | null>(null);
  const [editando, setEditando] = useState<AmostraProtocolo | null>(null);
  const [vinculando, setVinculando] = useState<AmostraProtocolo | null>(null);
  /** Editar / excluir a OS inteira (da lista ou de dentro dela). */
  const [gerenciando, setGerenciando] = useState<{ tipo: "editar" | "excluir"; os: string } | null>(null);
  const [excluindoAmostra, setExcluindoAmostra] = useState<AmostraParaExcluir | null>(null);

  const carregarLista = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.protocolo.listar();
      setLista(r.os);
      setEtapas(r.etapas);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarDetalhe = useCallback(async (os: string) => {
    setErroDetalhe(null);
    try {
      setDetalhe(await api.protocolo.os(os));
    } catch (e) {
      setDetalhe(null);
      setErroDetalhe(e instanceof ApiError ? e.message : "Não foi possível abrir a OS.");
    }
  }, []);

  useEffect(() => {
    void carregarLista();
  }, [carregarLista]);

  /* Trocar de OS limpa o aviso da anterior — menos o que foi deixado para a
     OS que está abrindo agora (a "OS criada" ao sair do pop-up de criação). */
  const mensagemAoAbrir = useRef<string | null>(null);
  useEffect(() => {
    setMensagem(mensagemAoAbrir.current);
    mensagemAoAbrir.current = null;
    setDetalhe(null);
    if (osAberta) void carregarDetalhe(osAberta);
  }, [osAberta, carregarDetalhe]);

  /* Pedido vindo das Análises ("Nova amostra"): abre a criação, já na OS. */
  useEffect(() => {
    if (!pedidoNovaOs) return;
    if (pedidoNovaOs.os) setOsAberta(pedidoNovaOs.os);
    setNovaOs({
      os: pedidoNovaOs.os ?? "",
      cliente: pedidoNovaOs.cliente ?? "",
      existente: !!pedidoNovaOs.os,
    });
    onPedidoAtendido?.();
  }, [pedidoNovaOs?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Depois de qualquer gravação: a OS aberta, a lista e o menu. */
  async function recarregarTudo() {
    if (osAberta) await carregarDetalhe(osAberta);
    await carregarLista();
    onDataChanged();
  }

  const contagem = useMemo(() => {
    const c: Partial<Record<EtapaProtocolo, number>> = {};
    for (const o of lista) c[o.etapa] = (c[o.etapa] ?? 0) + 1;
    return c;
  }, [lista]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return lista.filter((o) => {
      if (filtro === "abertas" && o.etapa === "relatorio_gerado") return false;
      if (filtro !== "abertas" && filtro !== "todas" && o.etapa !== filtro) return false;
      if (!q) return true;
      return [o.os_numero, o.cliente].some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [lista, filtro, busca]);

  async function desfazer(a: AmostraProtocolo) {
    if (!confirm(`Desfazer a entrada de ${a.nome}? A amostra volta para "pendente" e sai das Análises.`)) return;
    setOcupado(a.id);
    try {
      await api.protocolo.desfazerEntrada(a.id);
      setMensagem(`Entrada de ${a.nome} desfeita — a amostra voltou para pendente.`);
      await recarregarTudo();
    } catch (e) {
      setMensagem(e instanceof ApiError ? e.message : "Não foi possível desfazer a entrada.");
    } finally {
      setOcupado(null);
    }
  }

  /** Abre a confirmação de excluir a amostra (com o que se perde junto). */
  function excluir(a: AmostraProtocolo) {
    setExcluindoAmostra({
      id: a.id,
      nome: a.nome,
      os: a.os_numero,
      entregue: !!a.despachado_em || a.status === "Despachada",
      comTrabalho: a.concluidas.length + a.preenchidas.length + a.com_parecer.length > 0,
      ultima: (detalhe?.amostras.length ?? 1) <= 1,
    });
  }

  /**
   * Depois de editar a OS. Renumerada, a OS aberta passa a ser a do número
   * novo (a antiga não existe mais) — o aviso vai junto para a tela dela.
   */
  function aposEditarOs(novo: string, m: string) {
    setGerenciando(null);
    if (osAberta && novo !== osAberta) {
      mensagemAoAbrir.current = m;
      setOsAberta(novo);
      void carregarLista();
      onDataChanged();
    } else if (osAberta) {
      setMensagem(m);
      void recarregarTudo();
    } else {
      setMensagem(m);
      void carregarLista();
      onDataChanged();
    }
  }

  /** Depois de excluir a OS (ou a última amostra dela): volta para a lista com o aviso. */
  function voltarParaListaCom(m: string) {
    if (osAberta) {
      mensagemAoAbrir.current = m;
      setOsAberta(null);
    } else {
      setMensagem(m);
    }
    void carregarLista();
    onDataChanged();
  }

  async function gerarRelatorio(a: AmostraProtocolo) {
    setOcupado(a.id);
    try {
      await api.analises.gerarRelatorio(a.id, a.os_numero);
      setMensagem(`Relatório de ${a.nome} gerado.`);
      await recarregarTudo();
    } catch (e) {
      setMensagem(e instanceof ApiError ? e.message : "Não foi possível gerar o relatório.");
    } finally {
      setOcupado(null);
    }
  }

  /** O laudo que JÁ SAIU — baixa o gravado, sem gerar de novo. */
  async function baixarEntregue(a: AmostraProtocolo) {
    setOcupado(a.id);
    try {
      await api.analises.baixarLaudo(a.id, a.laudo_nome ?? `analise-oleo-OS-${a.os_numero}.docx`);
    } catch (e) {
      setMensagem(e instanceof ApiError ? e.message : "Não foi possível baixar o laudo.");
    } finally {
      setOcupado(null);
    }
  }

  /** Os relatórios da OS inteira: um .docx por amostra com o diagnóstico fechado. */
  async function gerarRelatoriosDaOs() {
    if (!detalhe) return;
    const prontas = detalhe.amostras.filter((a) => a.diag_finalizado_em && !a.despachado_em);
    if (prontas.length === 0) return;
    setOcupado(-1);
    let feitos = 0;
    try {
      for (const a of prontas) {
        await api.analises.gerarRelatorio(a.id, a.os_numero);
        feitos++;
      }
      setMensagem(`${feitos} relatório(s) da OS gerado(s).`);
    } catch (e) {
      setMensagem(
        `${feitos} de ${prontas.length} gerado(s) — ` +
          (e instanceof ApiError ? e.message : "falha ao gerar o restante."),
      );
    } finally {
      setOcupado(null);
      await recarregarTudo();
    }
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={carregarLista} />;

  // Amostra entregue não se regera: o laudo dela é o que o cliente recebeu.
  const prontasParaRelatorio =
    detalhe?.amostras.filter((a) => a.diag_finalizado_em && !a.despachado_em).length ?? 0;
  const pendentesNaOs = detalhe?.amostras.filter((a) => !a.entrada_lab_em) ?? [];
  const ultimoRec = detalhe?.recebimentos[0];

  return (
    <div className="crud">
      {/* ===================== NÍVEL 1: as OS ===================== */}
      {!osAberta && (
        <>
          <div className="an-resumo prot-etapas">
            {etapas.map((e) => (
              <button
                key={e.key}
                className={`an-resumo-item prot-etapa ${filtro === e.key ? "destaque" : ""} ${
                  e.key === "pendente" && (contagem.pendente ?? 0) > 0 ? "prot-etapa-pendente" : ""
                }`}
                onClick={() => setFiltro(filtro === e.key ? "abertas" : e.key)}
                title={filtro === e.key ? "Mostrar todas em aberto" : `Só as OS em “${e.rotulo}”`}
              >
                <strong>{contagem[e.key] ?? 0}</strong>
                {e.rotulo}
              </button>
            ))}
          </div>

          {mensagem && (
            <div className="modal-notice prot-mensagem">
              <span className="modal-notice-ic">ℹ️</span>
              <span>{mensagem}</span>
              <button className="icon-btn" onClick={() => setMensagem(null)} title="Fechar aviso">
                <IconClose size={15} />
              </button>
            </div>
          )}

          <div className="crud-toolbar">
            <div className="search-wrap">
              <IconSearch size={17} />
              <input
                className="search"
                placeholder="Buscar OS ou cliente..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>
            <div className="crud-filtros">
              <label className="crud-filtro">
                <span>Situação</span>
                <select
                  className="crud-select"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value as Filtro)}
                >
                  <option value="abertas">Em aberto (sem relatório)</option>
                  {etapas.map((e) => (
                    <option key={e.key} value={e.key}>
                      {e.rotulo}
                    </option>
                  ))}
                  <option value="todas">Todas</option>
                </select>
              </label>
            </div>
            <div className="toolbar-right">
              <span className="badge">{filtradas.length} OS</span>
              <button className="btn-primary" onClick={() => setNovaOs({ os: "", cliente: "", existente: false })}>
                <IconPlus size={17} /> Nova OS
              </button>
            </div>
          </div>

          <div className="table-wrap">
            {loading ? (
              <div className="empty">Carregando...</div>
            ) : filtradas.length === 0 ? (
              <div className="empty">
                {busca
                  ? "Nenhuma OS encontrada para a busca."
                  : filtro === "pendente"
                    ? "Nenhuma OS esperando entrada no laboratório."
                    : "Nenhuma OS aqui. As solicitações do campo chegam sozinhas; amostra de balcão entra por “Nova OS”."}
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>OS</th>
                    <th>Cliente</th>
                    <th>Origem</th>
                    <th>Amostras</th>
                    <th>Ensaios</th>
                    <th>Solicitada</th>
                    <th>Entrada</th>
                    <th>Situação</th>
                    <th className="col-actions">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((o) => (
                    <tr
                      key={o.os_numero}
                      className="row-clickable"
                      onClick={() => setOsAberta(o.os_numero)}
                      title="Abrir a OS"
                    >
                      <td>
                        <strong className="an-os-num">OS {o.os_numero}</strong>
                      </td>
                      <td>{o.cliente || "—"}</td>
                      <td>{o.origens.map((x) => ROTULO_ORIGEM[x] ?? x).join(", ") || "—"}</td>
                      <td>
                        <span className="an-qtd-chip">{o.amostras}</span>
                        {o.pendentes > 0 && o.pendentes < o.amostras && (
                          <span className="an-nota">{o.pendentes} sem entrada</span>
                        )}
                        {o.divergente && <span className="an-nota prot-alerta">divergência na conferência</span>}
                      </td>
                      <td>
                        <span className="an-selos">
                          {o.ensaios.map((k) => (
                            <SeloEnsaio key={k} chave={k} />
                          ))}
                        </span>
                      </td>
                      <td>{formatDate(o.solicitado_em)}</td>
                      <td>{o.entrada ? formatDate(o.entrada) : "—"}</td>
                      <td>
                        <span className={`pill pill-${tomDaEtapa(o.etapa)}`}>{o.etapa_rotulo}</span>
                        {o.sem_equipamento > 0 && (
                          <div className="an-nota prot-alerta">
                            {o.sem_equipamento} sem equipamento vinculado
                          </div>
                        )}
                      </td>
                      <td
                        className="col-actions"
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="icon-btn"
                          title="Editar a OS (número, cliente, solicitante, prazos)"
                          onClick={() => setGerenciando({ tipo: "editar", os: o.os_numero })}
                        >
                          <IconEdit size={17} />
                        </button>
                        {podeExcluir && (
                          <button
                            className="icon-btn icon-btn-danger"
                            title="Excluir a OS inteira"
                            onClick={() => setGerenciando({ tipo: "excluir", os: o.os_numero })}
                          >
                            <IconTrash size={17} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ===================== NÍVEL 2: a OS aberta ===================== */}
      {osAberta && (
        <>
          <div className="an-trilha">
            <button className="btn-secondary btn-sm" onClick={() => setOsAberta(null)}>
              ← Voltar
            </button>
            <span className="an-trilha-item" onClick={() => setOsAberta(null)} role="button" tabIndex={0}>
              Protocolo
            </span>
            <span className="an-trilha-sep">›</span>
            <strong>OS {osAberta}</strong>
          </div>

          {erroDetalhe ? (
            <div className="empty">{erroDetalhe}</div>
          ) : !detalhe ? (
            <div className="empty">Carregando...</div>
          ) : (
            <>
              <div className="an-os-cabecalho">
                <div className="an-os-cab-txt">
                  <h3>
                    OS {detalhe.os_numero}
                    <span className="an-os-cab-cliente">{detalhe.cliente || "Cliente não informado"}</span>
                  </h3>
                  <p className="muted">
                    <span className={`pill pill-${tomDaEtapa(detalhe.etapa)}`}>{detalhe.etapa_rotulo}</span>
                    {detalhe.amostras[0]?.solicitante ? ` · solicitante: ${detalhe.amostras[0].solicitante}` : ""}
                    {detalhe.amostras[0]?.prazo_retorno
                      ? ` · retorno até ${formatDate(detalhe.amostras[0].prazo_retorno)}`
                      : ""}
                  </p>
                </div>
                <div className="an-resumo">
                  <span className="an-resumo-item destaque">
                    <strong>{detalhe.amostras.length}</strong>
                    Equipamento(s) / amostra(s)
                  </span>
                  <span className={`an-resumo-item ${pendentesNaOs.length > 0 ? "prot-atencao" : ""}`}>
                    <strong>{pendentesNaOs.length}</strong>
                    Pendente(s) de entrada
                  </span>
                  {ultimoRec && (
                    <span className={`an-resumo-item ${ultimoRec.quantidade_prevista !== ultimoRec.quantidade_recebida ? "prot-atencao" : ""}`}>
                      <strong>
                        {ultimoRec.quantidade_recebida}/{ultimoRec.quantidade_prevista}
                      </strong>
                      Recebidas/previstas (última entrada)
                    </span>
                  )}
                  {detalhe.material && detalhe.material.previsto > 0 && (
                    <span className="an-resumo-item">
                      <strong>
                        {detalhe.material.bipados}/{detalhe.material.previsto}
                      </strong>
                      Frascos bipados (remessa)
                    </span>
                  )}
                </div>
              </div>

              {mensagem && (
                <div className="modal-notice prot-mensagem">
                  <span className="modal-notice-ic">ℹ️</span>
                  <span>{mensagem}</span>
                  <button className="icon-btn" onClick={() => setMensagem(null)} title="Fechar aviso">
                    <IconClose size={15} />
                  </button>
                </div>
              )}

              {pendentesNaOs.length > 0 && (
                <PainelEntrada
                  // Recomeça (data, previstas, marcadas) a cada entrada parcial:
                  // as que sobraram são outra conferência.
                  key={`${detalhe.os_numero}|${pendentesNaOs.map((a) => a.id).join(",")}`}
                  detalhe={detalhe}
                  onVincular={(a) => setVinculando(a)}
                  onRegistrado={async (m) => {
                    setMensagem(m);
                    await recarregarTudo();
                  }}
                />
              )}

              <div className="dash-topo">
                <h3 className="dash-secao">Equipamentos da OS</h3>
                <div className="prot-acoes-os">
                  {prontasParaRelatorio > 0 && (
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => void gerarRelatoriosDaOs()}
                      disabled={ocupado !== null}
                      title="Gera e baixa o laudo (.docx) de cada amostra com o diagnóstico finalizado"
                    >
                      <IconDownload size={15} /> Gerar relatórios da OS ({prontasParaRelatorio})
                    </button>
                  )}
                  <button
                    className="btn-secondary btn-sm"
                    onClick={() =>
                      setNovaOs({ os: detalhe.os_numero, cliente: detalhe.cliente ?? "", existente: true })
                    }
                  >
                    <IconPlus size={15} /> Acrescentar equipamento
                  </button>
                  <button
                    className="btn-secondary btn-sm"
                    title="Número, cliente, solicitante e prazos — em todas as amostras da OS"
                    onClick={() => setGerenciando({ tipo: "editar", os: detalhe.os_numero })}
                  >
                    <IconEdit size={15} /> Editar OS
                  </button>
                  {podeExcluir && (
                    <button
                      className="btn-secondary btn-sm"
                      title="Apaga a OS inteira do laboratório"
                      onClick={() => setGerenciando({ tipo: "excluir", os: detalhe.os_numero })}
                    >
                      <IconTrash size={15} /> Excluir OS
                    </button>
                  )}
                </div>
              </div>

              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Amostra</th>
                      <th>Equipamento</th>
                      <th>Ensaios</th>
                      <th>Coleta</th>
                      <th>Entrada</th>
                      <th>Situação</th>
                      <th className="col-actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.amostras.map((a) => {
                      const podeDesfazer =
                        !!a.entrada_lab_em &&
                        a.etapa === "recebida";
                      return (
                        <tr key={a.id}>
                          <td>
                            <strong>{a.nome}</strong>
                            {a.origem && <div className="an-nota">{ROTULO_ORIGEM[a.origem] ?? a.origem}</div>}
                          </td>
                          <td>
                            {a.equip ? (
                              <>
                                {descreverEquip(a.equip)}
                                {a.equip.proprietario && <div className="an-nota">{a.equip.proprietario}</div>}
                              </>
                            ) : (
                              <button className="btn-secondary btn-sm" onClick={() => setVinculando(a)}>
                                Vincular equipamento
                              </button>
                            )}
                          </td>
                          <td>
                            <span className="an-selos">
                              {a.ensaios.map((k) => (
                                <SeloEnsaio key={k} chave={k} feito={a.concluidas.includes(k)} />
                              ))}
                            </span>
                          </td>
                          <td>
                            {a.data_coleta_texto || "—"}
                            {(a.temperatura_coleta || a.condicao_oleo) && (
                              <div className="an-nota">
                                {[a.temperatura_coleta ? `${a.temperatura_coleta} °C` : null, a.condicao_oleo]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            )}
                          </td>
                          <td>
                            {a.entrada_lab_em ? formatDate(a.entrada_lab_em) : "—"}
                            {a.entrada_lab_por && <div className="an-nota">{a.entrada_lab_por}</div>}
                          </td>
                          <td>
                            <span className={`pill pill-${tomDaEtapa(a.etapa)}`}>{a.etapa_rotulo}</span>
                          </td>
                          <td className="col-actions">
                            {!a.despachado_em && (
                              <button
                                className="icon-btn"
                                title="Dados da coleta (nº, data, temperatura, condição do óleo)"
                                onClick={() => setEditando(a)}
                              >
                                <IconEdit size={17} />
                              </button>
                            )}
                            {a.equip && !a.despachado_em && (
                              <button
                                className="icon-btn"
                                title="Trocar o equipamento vinculado"
                                onClick={() => setVinculando(a)}
                              >
                                <IconClipboard size={17} />
                              </button>
                            )}
                            {a.diag_finalizado_em && !a.despachado_em && (
                              <button
                                className="icon-btn icon-btn-brand"
                                title="Gerar o relatório (laudo .docx) desta amostra"
                                onClick={() => void gerarRelatorio(a)}
                                disabled={ocupado !== null}
                              >
                                <IconDownload size={17} />
                              </button>
                            )}
                            {a.despachado_em && a.laudo_nome && (
                              <button
                                className="icon-btn"
                                title="Baixar o laudo entregue (o documento que o cliente recebeu)"
                                onClick={() => void baixarEntregue(a)}
                                disabled={ocupado !== null}
                              >
                                <IconDownload size={17} />
                              </button>
                            )}
                            {podeDesfazer && (
                              <button
                                className="icon-btn"
                                title="Desfazer a entrada (volta para pendente — só antes de a bancada começar)"
                                onClick={() => void desfazer(a)}
                                disabled={ocupado !== null}
                              >
                                <IconRefresh size={17} />
                              </button>
                            )}
                            {podeExcluir && (
                              <button
                                className="icon-btn icon-btn-danger"
                                title="Excluir a amostra"
                                onClick={() => excluir(a)}
                                disabled={ocupado !== null}
                              >
                                <IconTrash size={17} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {detalhe.recebimentos.length > 0 && (
                <>
                  <div className="dash-topo">
                    <h3 className="dash-secao">Conferências de entrada</h3>
                    <span className="muted dash-dica">o que o campo previu × o que chegou, em cada remessa</span>
                  </div>
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Entrada</th>
                          <th>Previstas</th>
                          <th>Recebidas</th>
                          <th>Divergência</th>
                          <th>Amostras liberadas</th>
                          <th>Conferido por</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalhe.recebimentos.map((r) => (
                          <tr key={r.id}>
                            <td>{formatDate(r.data_entrada)}</td>
                            <td>{r.quantidade_prevista}</td>
                            <td>
                              {r.quantidade_recebida}
                              {r.quantidade_recebida !== r.quantidade_prevista && (
                                <span className="pill pill-warn an-pill-mini">diverge</span>
                              )}
                            </td>
                            <td>
                              {r.divergencia || "—"}
                              {r.observacao && <div className="an-nota">{r.observacao}</div>}
                            </td>
                            <td>{r.amostras}</td>
                            <td>
                              {r.recebido_por || "—"}
                              <div className="an-nota">{new Date(r.registrado_em).toLocaleString("pt-BR")}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {novaOs && (
        <ModalNovaOs
          key={`${novaOs.os}|${novaOs.existente}`}
          inicial={novaOs}
          onCancel={() => setNovaOs(null)}
          onCriada={async (os, m) => {
            setNovaOs(null);
            if (osAberta === os) {
              setMensagem(m);
              await recarregarTudo();
            } else {
              mensagemAoAbrir.current = m;
              setOsAberta(os);
              await carregarLista();
              onDataChanged();
            }
          }}
        />
      )}
      {editando && (
        <ModalColeta
          amostra={editando}
          onCancel={() => setEditando(null)}
          onSalvo={async () => {
            setEditando(null);
            setMensagem("Dados da coleta salvos.");
            await recarregarTudo();
          }}
        />
      )}
      {gerenciando?.tipo === "editar" && (
        <ModalEditarOs os={gerenciando.os} onCancel={() => setGerenciando(null)} onSalvo={aposEditarOs} />
      )}
      {gerenciando?.tipo === "excluir" && (
        <ModalExcluirOs
          os={gerenciando.os}
          onCancel={() => setGerenciando(null)}
          onExcluida={(m) => {
            setGerenciando(null);
            voltarParaListaCom(m);
          }}
        />
      )}
      {excluindoAmostra && (
        <ModalExcluirAmostra
          amostra={excluindoAmostra}
          onCancel={() => setExcluindoAmostra(null)}
          onExcluida={(m, ultima) => {
            setExcluindoAmostra(null);
            if (ultima) {
              voltarParaListaCom(m);
            } else {
              setMensagem(m);
              void recarregarTudo();
            }
          }}
        />
      )}
      {vinculando && (
        <ModalVincular
          amostra={vinculando}
          onCancel={() => setVinculando(null)}
          onVinculado={async () => {
            setVinculando(null);
            setMensagem("Equipamento vinculado — a identificação do laudo foi completada com a ficha dele.");
            await recarregarTudo();
          }}
        />
      )}
    </div>
  );
}
